import { useId, useRef, type ComponentType, type KeyboardEvent, type ReactNode } from "react";
import { motion, LayoutGroup } from "framer-motion";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { haptics } from "@/lib/haptics";

/**
 * Segmented pill control — the pattern that appears three times in the app
 * today (expenses By category / Who owes who, profile theme toggle, plan
 * sub-tabs). Selection glides via framer-motion `layoutId` so the thumb
 * animates between options instead of hard-swapping.
 *
 * Framer-motion's `LayoutGroup` scopes the shared `layoutId` — that means
 * multiple Segmented controls on the same page each animate their own
 * thumb without cross-talk, but a caller can pass `groupId` to force
 * cross-instance sharing (rare, only if two visually stacked controls
 * should swap thumbs — none today).
 *
 * The reduced-motion killswitch is inherited from the global `*`
 * transition reset in app.css.
 */

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  icon?: LucideIcon | ComponentType<{ className?: string }>;
  /** Extra accessible label; falls back to `label` when omitted. */
  ariaLabel?: string;
}

interface SegmentedProps<T extends string> {
  value: T;
  onChange: (next: T) => void;
  options: readonly SegmentedOption<T>[];
  /** Passed to the underlying `role="tablist"` — set on segmented tabs. */
  ariaLabel?: string;
  /** Override the auto-generated layoutId (rarely needed). */
  groupId?: string;
  className?: string;
  /** Compact = smaller pill (used inside sheets and dense summaries). */
  size?: "md" | "sm";
}

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  groupId,
  className,
  size = "md",
}: SegmentedProps<T>) {
  const autoId = useId();
  const thumbId = `segmented-thumb-${groupId ?? autoId}`;
  const tabsRef = useRef<Array<HTMLButtonElement | null>>([]);

  // WAI-ARIA tabs pattern: ArrowLeft / ArrowRight moves between tabs
  // and activates. Home / End jump to the first / last tab.
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const idx = options.findIndex((o) => o.value === value);
    if (idx < 0) return;
    let next = idx;
    if (e.key === "ArrowRight") next = (idx + 1) % options.length;
    else if (e.key === "ArrowLeft") next = (idx - 1 + options.length) % options.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = options.length - 1;
    else return;
    e.preventDefault();
    haptics.select();
    onChange(options[next]!.value);
    tabsRef.current[next]?.focus();
  };

  return (
    <LayoutGroup id={thumbId}>
      <div
        role="tablist"
        aria-label={ariaLabel}
        onKeyDown={onKey}
        className={cn(
          "flex gap-1 rounded-full bg-bg-sunken p-1",
          className,
        )}
      >
        {options.map((opt, i) => {
          const on = value === opt.value;
          const Icon = opt.icon;
          return (
            <button
              key={opt.value}
              ref={(el) => {
                tabsRef.current[i] = el;
              }}
              role="tab"
              type="button"
              aria-selected={on}
              aria-label={opt.ariaLabel}
              tabIndex={on ? 0 : -1}
              onClick={() => {
                if (on) return;
                haptics.select();
                onChange(opt.value);
              }}
              className={cn(
                "relative flex-1 inline-flex items-center justify-center gap-1.5 rounded-full font-semibold transition-colors",
                "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
                size === "sm" ? "py-1.5 text-[12px]" : "py-2 text-[13px]",
                on ? "text-ink" : "text-ink-subtle hover:text-ink",
              )}
            >
              {on ? (
                <motion.span
                  layoutId={thumbId}
                  aria-hidden
                  className="absolute inset-0 rounded-full bg-bg-elevated shadow-warm-1"
                  transition={{ type: "spring", stiffness: 400, damping: 34 }}
                />
              ) : null}
              <span className="relative inline-flex items-center gap-1.5">
                {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
                {opt.label}
              </span>
            </button>
          );
        })}
      </div>
    </LayoutGroup>
  );
}
